import { FormTextInput } from '@portal_components/components/FormTextInput';
import { PageHeading } from '@portal_components/components/PageHeading';
import { FormButton } from '@portal_components/components/FormButton';
import { Button } from '@portal_components/components/Button';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EHttpMethod, usePortalQuery } from '../hooks/usePortalQuery';
import { EMpanAssignmentMode, MeterPointDetails, MpanAssignmentPageForm } from '../types';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { MeterPointDetailsFormWrapper } from '../elements/MeterPointDetailsFormWrapper';
import { EUserRole } from '@portal_components/types';
import { useUserInfo } from '../hooks/useUserInfo';

const schema = Yup.object<
  MpanAssignmentPageForm,
  { [Key in keyof MpanAssignmentPageForm]: Yup.AnySchema<MpanAssignmentPageForm[Key]> }
>({
  mpan: Yup.string()
    .required()
    .matches(/[0-9]{13}/g, 'Mpan is invalid, it must contain 13 digits'),
  mode: Yup.string<EMpanAssignmentMode>().required()
});

interface MpanAssignmentPageFormProps {
  forcedMpan?: string;
  forcedMode?: EMpanAssignmentMode;
}

export const MpanAssignmentPage = ({ forcedMpan, forcedMode }: MpanAssignmentPageFormProps) => {
  const { userInfo } = useUserInfo();
  const [mpanSubmitted, setMpanSubmitted] = useState<boolean>(false);

  const methods = useForm<MpanAssignmentPageForm>({
    values: {
      mpan: forcedMpan ?? '',
      mode: forcedMode ?? EMpanAssignmentMode.VIEW
    },
    resolver: yupResolver<MpanAssignmentPageForm>(schema as any)
  });

  const [mode, mpan] = methods.watch(['mode', 'mpan']);

  const { data: existingMeterPointDetails, isLoading: existingMeterPointDetailsLoading } = usePortalQuery<{
    [customerGroupId: string]: { [contractId: string]: MeterPointDetails };
  }>(
    EHttpMethod.GET,
    `meter-point-details/map/by-mprn/${mpan}?mode=${mode}`,
    `get-existing-details-for-mpan-${mpan}`,
    mpanSubmitted && !!mpan && [EMpanAssignmentMode.EDIT, EMpanAssignmentMode.VIEW].includes(mode),
    undefined
  );

  const handleSearch = () => {
    setMpanSubmitted(true);
  };

  const handleReset = () => {
    setMpanSubmitted(false);
    methods.reset();
  };

  //appointment loading only for NEW
  const showForm =
    mpanSubmitted &&
    (mode === EMpanAssignmentMode.NEW ||
      (!existingMeterPointDetailsLoading &&
        existingMeterPointDetails &&
        [EMpanAssignmentMode.EDIT, EMpanAssignmentMode.VIEW].includes(mode)));

  const pageTitle = useMemo(() => {
    switch (mode) {
      case EMpanAssignmentMode.NEW:
        return 'Create New Site Meter Point Details';
      case EMpanAssignmentMode.EDIT:
        return 'Edit Existing Site Meter Point Details';
      default:
        return 'Search Existing Site Meter Point Details';
    }
  }, [mode]);

  const modeOptions = useMemo(() => {
    const defaultModes = [{ title: 'Search', value: EMpanAssignmentMode.VIEW }];
    if (!userInfo || userInfo.role === EUserRole.READONLY_USER) {
      return defaultModes;
    }
    return [
      ...defaultModes,
      { title: 'New', value: EMpanAssignmentMode.NEW },
      { title: 'Edit', value: EMpanAssignmentMode.EDIT }
    ];
  }, [userInfo]);

  return (
    <div className="flex flex-col justify-start">
      <PageHeading
        title={pageTitle}
        navigationButtons={[
          {
            icon: '/search.svg',
            title: 'Search MPANs By Customer Contract',
            path: '/search'
          },
          {
            icon: '/fileTable.svg',
            title: 'Assign Multiple MPANs',
            path: '/bulk-assign',
            disabled: !userInfo || userInfo.role === EUserRole.READONLY_USER
          },
          {
            icon: '/attention.svg',
            title: 'View Exceptions',
            path: '/exceptions'
          }
        ]}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSearch)}>
          <FormRadioButtonGroup
            title="Search, New or Edit"
            name="mode"
            buttons={modeOptions}
            readonly={mpanSubmitted || !!forcedMode}
          />
          <div className="flex flex-row">
            <FormTextInput title="MPAN" name="mpan" readonly={mpanSubmitted || !!forcedMpan} />
            <div className="pl-2 pt-2 pb-4 mb-2">
              <FormButton type="submit" primary>
                Search
              </FormButton>
            </div>
            <div className="pl-2 pt-2 pb-4 mb-2">
              <Button text="Reset" primary={false} onClick={handleReset} />
            </div>
          </div>
        </form>
      </FormProvider>
      {[EMpanAssignmentMode.EDIT, EMpanAssignmentMode.VIEW].includes(mode) &&
        mpanSubmitted &&
        existingMeterPointDetailsLoading && (
          <div className="flex flex-row justify-center">Loading existing meter point details...</div>
        )}
      {[EMpanAssignmentMode.EDIT, EMpanAssignmentMode.VIEW].includes(mode) &&
        mpanSubmitted &&
        !existingMeterPointDetails &&
        !existingMeterPointDetailsLoading && (
          <div className="flex flex-row justify-center">No existing meter point details for MPAN: {mpan}</div>
        )}
      {showForm && (
        <MeterPointDetailsFormWrapper
          mode={mode}
          mpan={mpan}
          meterPointDetailsForMpan={
            [EMpanAssignmentMode.EDIT, EMpanAssignmentMode.VIEW].includes(mode)
              ? existingMeterPointDetails
              : undefined
          }
          handleReset={handleReset}
        />
      )}
    </div>
  );
};
